import {
  AgentConfigPresetThumbnailCreate,
  AgentConfigPresetThumbnailDestroy
} from '@tryiris-ai/mailroom-helper/agentConfig';
import type { SignedUrl } from './types.js';
import { wrapApiCall } from '#~/api/index.js';
import type { ByteId } from '#~/api/index.js';
import { nats } from '#~/nats/index.js';

/**
 * Creates a signed URL that can be used for uploading an image thumbnail.
 *
 * @param agentId - Agent ID.
 * @param deviceId - Device ID.
 * @param presetId - Preset ID.
 * @param byteSize - Size of the thumbnail image in bytes.
 * @param token - Token.
 * @returns Signed AWS S3 URL.
 */
export const createSignedUrl = async (
  agentId: ByteId,
  deviceId: ByteId,
  presetId: ByteId,
  byteSize: number,
  token: string
): Promise<SignedUrl> => {
  const result = await wrapApiCall(async () =>
    AgentConfigPresetThumbnailCreate.request(nats.cloudConnection(), {
      agentId,
      byteSize,
      deviceId,
      presetId,
      token
    })
  );

  return {
    headers: result.signedUploadHeaders,
    publicUrl: result.publicAccessUrl,
    uploadUrl: window.atob(result.signedUploadUrl)
  };
};

/**
 * Deletes an existing preset thumbnail.
 *
 * @param agentId - Agent ID.
 * @param deviceId - Device ID.
 * @param presetId - Preset ID.
 * @param token - Token.
 * @returns Signed AWS S3 URL.
 */
export const deleteThumbnail = async (
  agentId: ByteId,
  deviceId: ByteId,
  presetId: ByteId,
  token: string
): Promise<unknown> =>
  wrapApiCall(async () =>
    AgentConfigPresetThumbnailDestroy.request(nats.cloudConnection(), {
      agentId,
      deviceId,
      presetId,
      token
    })
  );
